import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Barcode, ArrowCounterClockwise, Printer, ArrowLineDown, ArrowLineUp,
  ClipboardText, HandArrowDown, HandArrowUp, Lightbulb, CaretUp, CaretDown,
  UserPlus, ArrowsClockwise, MapPin, MagnifyingGlass, ChartBar,
  ArrowsLeftRight, Trash, Stack,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, canEditIt } from '@/auth/AuthContext';
import { DepartmentProvider, useDepartment } from '@/pages/mfg/DepartmentContext';
import { DepartmentSelector, DepartmentPicker } from '@/pages/mfg/DepartmentSelector';
import { mfgApi } from '@/pages/mfg/mfgApi';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildLotLabel } from '@/components/labels/labelSizes';
import {
  scannerApi, scanFeedback, scanCommitFeedback, STOCK_MODES,
  EMPLOYEE_REQUIRED_MODES, MODE_BADGE,
} from './scannerApi';
import { PROFILES, PROFILE_MAP, allowedKinds, KIND_LABEL } from './scannerProfiles';
import ScannerScanner from './ScannerScanner';
import ScannerCart from './ScannerCart';
import BorrowerPicker from './BorrowerPicker';
import VerifyReportDialog from './VerifyReportDialog';
import { formatShort } from '@/utils/datetime';
import { normalizeTehai, tehaiError, TEHAI_HINT } from '@/lib/tehaiNo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_ICON = {
  stock_in: ArrowLineDown, stock_out: ArrowLineUp, stocktake: ClipboardText,
  lend: HandArrowUp, return: HandArrowDown,
  assign: UserPlus, verify: ArrowsClockwise, move: MapPin, lookup: MagnifyingGlass,
  eng_use: HandArrowUp, eng_return: HandArrowDown, eng_scrap: Trash, bihin_stock: Stack,
};

const GUIDE_STEPS = ['scanner.guide.s1', 'scanner.guide.s2', 'scanner.guide.s3', 'scanner.guide.s4', 'scanner.guide.s5'];

// Scanner Hub — always shown when the scanner opens (no localStorage memory,
// per spec: every visit starts from the profile selection screen).
function ScannerHub({ availability, hints, onPick }) {
  const { t } = useT();
  return (
    <div className="page-enter max-w-4xl mx-auto pt-8" data-testid="scanner-context-gate">
      <p className="text-center text-sm text-zinc-500 mb-8">{t('scanner.gate.subtitle')}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {PROFILES.map((p) => {
          const enabled = availability[p.id];
          const Icon = p.icon;
          return (
            <button key={p.id} type="button" onClick={() => onPick(p.id)} disabled={!enabled}
              data-testid={`scanner-gate-${p.id}`}
              className={`group text-left rounded-lg border-2 p-6 transition-all ${
                enabled
                  ? `bg-white border-zinc-200 ${p.theme.cardHover} hover:shadow-lg hover:-translate-y-1`
                  : 'bg-zinc-50 border-zinc-100 opacity-50 cursor-not-allowed'
              }`}>
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-colors ${p.theme.iconBg}`}>
                <Icon size={30} weight="duotone" className={p.theme.icon} />
              </div>
              <h2 className="text-lg font-bold text-zinc-900 mb-1">{t(p.titleKey)}</h2>
              <p className="text-sm text-zinc-500 mb-3">{t(p.descKey)}</p>
              <ul className="text-xs text-zinc-500 space-y-0.5">
                {p.bullets.map((b) => <li key={b}>・{b}</li>)}
              </ul>
              {!enabled && p.lockHint && <p className="text-[11px] text-rose-500 mt-3">{p.lockHint}</p>}
              {enabled && hints[p.id] && <p className="text-[11px] text-amber-600 mt-3">{hints[p.id]}</p>}
            </button>
          );
        })}
      </div>
      <p className="text-center text-xs text-zinc-400 mt-6">{t('scanner.gate.footer')}</p>
    </div>
  );
}

// Small pill showing the current profile + a switch button back to the hub.
function ContextChip({ profile, onSwitch }) {
  const { t } = useT();
  const Icon = profile.icon;
  return (
    <button type="button" onClick={onSwitch} data-testid="scanner-context-switch"
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-semibold transition-colors ${profile.theme.chip}`}
      title="スキャナーを切り替える">
      <Icon size={14} weight="duotone" /> {t(profile.titleKey)}
      <ArrowsLeftRight size={12} weight="bold" className="opacity-60" />
    </button>
  );
}

function ScannerInner() {
  const { t } = useT();
  const { user } = useAuth();
  const { activeId, active, isAll, loading } = useDepartment();
  const navigate = useNavigate();

  // Always start at the hub — no saved context restoration.
  const [context, setContext] = useState(null);
  const [mode, setMode] = useState('lookup');

  // Access flags for the two capability-shaped profiles
  const [engMember, setEngMember] = useState(false);
  const [bihinEdit, setBihinEdit] = useState(false);
  useEffect(() => {
    axios.get(`${API}/engineering/access`).then((r) => setEngMember(!!(r.data?.is_member || r.data?.is_admin))).catch(() => {});
    axios.get(`${API}/bihin/access`).then((r) => setBihinEdit(!!(r.data?.can_edit || r.data?.is_admin))).catch(() => {});
  }, []);

  const availability = {
    mfg: canEditMfg(user),
    it: canEditIt(user),
    eng: true,
    bihin: true,
  };
  const hubHints = {
    eng: engMember ? null : '情報表示のみ利用できます（操作は技術メンバー）',
    bihin: bihinEdit ? null : '在庫調整は bihin_edit 権限が必要です',
  };

  const profile = context ? PROFILE_MAP[context] : null;
  const availableModes = useMemo(() => {
    if (!profile) return [];
    return profile.modes.filter((m) => {
      if (profile.memberModes?.includes(m) && !engMember) return false;
      if (profile.editModes?.includes(m) && !bihinEdit) return false;
      return true;
    });
  }, [profile, engMember, bihinEdit]);

  const [cart, setCart] = useState([]);
  const [borrower, setBorrower] = useState(null);         // shared for lend + assign
  const [moveLocation, setMoveLocation] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [committing, setCommitting] = useState(false);
  const [issueReason, setIssueReason] = useState('production');
  // TECHS の手配番号 — カートに1つ設定して、出庫の全行に同じ番号を付ける。
  const [tehaiNo, setTehaiNo] = useState('');
  const tehaiRequired = ['production', 'prototype'].includes(issueReason);
  const [lastSession, setLastSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [labelData, setLabelData] = useState(null);
  const [verifyReportOpen, setVerifyReportOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(() => {
    try {
      const saved = localStorage.getItem('scanner_guide_hidden');
      if (saved === '1') return false;
      if (saved === '0') return true;
      // No saved preference — default to collapsed on phones so the scanner
      // viewport stays above the fold, expanded on tablet+.
      return typeof window !== 'undefined' && window.innerWidth >= 640;
    } catch { return true; }
  });
  const toggleGuide = () => setShowGuide((v) => {
    const nv = !v;
    try { localStorage.setItem('scanner_guide_hidden', nv ? '0' : '1'); } catch { /* noop */ }
    return nv;
  });

  const pickContext = (c) => {
    setContext(c);
    setMode(PROFILE_MAP[c].defaultMode);
    setCart([]);
    setBorrower(null);
    setLastSession(null);
    setMoveLocation('');
  };
  const backToHub = () => {
    setContext(null);
    setCart([]);
    setBorrower(null);
    setLastSession(null);
  };

  const isStock = STOCK_MODES.includes(mode);
  const needsEmployee = EMPLOYEE_REQUIRED_MODES.includes(mode);

  const loadSessions = useCallback(() => {
    scannerApi.sessions({ limit: 12 }).then(setSessions).catch(() => {});
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => {
    if (context !== 'mfg' || !activeId || isAll) { setLocations([]); return; }
    mfgApi.locations().then((ls) => setLocations(Array.isArray(ls) ? ls : [])).catch(() => setLocations([]));
  }, [context, activeId, isAll]);

  const changeMode = (m) => {
    if (m === mode) return;
    setMode(m);
    setCart([]);
    setLastSession(null);
    setMoveLocation('');
    if (!EMPLOYEE_REQUIRED_MODES.includes(m)) setBorrower(null);
  };

  const addFromResolve = useCallback((res) => {
    // 4 · 場所ラベルを読んだら、その場所を選ぶ（入出庫・棚卸の置き場、移動先）。2026-11-05
    if (res.kind === 'location') {
      const loc = res.location || {};
      if (loc.active === false) {
        scanFeedback(false);
        toast.error(`「${loc.name || loc.code}」は無効な場所です`);
        return;
      }
      scanFeedback(true);
      if (mode === 'move') setMoveLocation(loc.path || loc.name || loc.code || '');
      else setLocationId(loc.id);
      toast.success(`場所: ${loc.path || loc.name}（${loc.code || '—'}）`);
      return;
    }
    const allowed = allowedKinds(context, mode);
    if (!allowed.includes(res.kind)) {
      scanFeedback(false);
      const lbl = KIND_LABEL[res.kind];
      toast.error(lbl ? `「${lbl}」のコードです。対応するスキャナーで実行してください` : t('scanner.wrongKind'));
      return;
    }

    if (isStock) {
      const it = res.item;
      if (!it) { scanFeedback(false); return; }
      setCart((prev) => {
        if (mode === 'stock_in') {
          const isLot = res.kind === 'lot';
          const key = isLot ? `${it.id}|${it.lot_no}` : it.id;
          const found = prev.find((l) => l.key === key);
          if (found) return prev.map((l) => l.key === key ? { ...l, quantity: Number(l.quantity || 0) + 1 } : l);
          return [...prev, { key, item_id: it.id, name: it.name, sku: it.sku, uom: it.uom, on_hand: it.on_hand,
            quantity: 1, isLot, lot_no: it.lot_no, lot_id: it.lot_id }];
        }
        if (mode === 'stock_out') {
          const key = it.id;
          const found = prev.find((l) => l.key === key);
          if (found) return prev.map((l) => l.key === key ? { ...l, quantity: Number(l.quantity || 0) + 1 } : l);
          return [...prev, { key, item_id: it.id, name: it.name, sku: it.sku, uom: it.uom, on_hand: it.on_hand, quantity: 1 }];
        }
        // stocktake
        const isLot = res.kind === 'lot';
        const key = isLot ? `lot|${it.lot_id}` : it.id;
        if (prev.find((l) => l.key === key)) return prev;
        return [...prev, { key, item_id: it.id, name: it.name, sku: it.sku, uom: it.uom, on_hand: it.on_hand,
          isLot, lot_id: it.lot_id, lot_no: it.lot_no, lot_quantity: it.lot_quantity,
          counted_qty: isLot ? it.lot_quantity : it.on_hand }];
      });
      scanFeedback(true, mode);
      return;
    }

    const a = res.asset;
    if (!a) { scanFeedback(false); return; }

    // 技術資産 — qty-based use / return / scrap
    if (res.kind === 'eng_asset') {
      if ((mode === 'eng_use' || mode === 'eng_scrap') && !(Number(a.qty_available) > 0)) {
        scanFeedback(false); toast.error(`${a.name}: 在庫がありません`); return;
      }
      if (mode === 'eng_return' && !(Number(a.qty_used) > 0)) {
        scanFeedback(false); toast.error(`${a.name}: 使用中の数量がありません`); return;
      }
      const key = `eng|${a.id}`;
      setCart((prev) => prev.find((l) => l.key === key) ? prev
        : [...prev, { key, asset_kind: 'eng', asset_id: a.id, asset_no: a.asset_no, name: a.name,
          uom: a.uom, qty_available: a.qty_available, qty_used: a.qty_used, status: a.status,
          quantity: 1, reason: '' }]);
      scanFeedback(true, mode);
      return;
    }

    // 備品 — lend / return / stock adjust / lookup
    if (res.kind === 'bihin') {
      if (mode === 'lend') {
        if (a.bihin_kind === 'consumable') { scanFeedback(false); toast.error('消耗品は貸出できません（在庫調整を使ってください）'); return; }
        if (a.lent_out) { scanFeedback(false); toast.error(`${a.name}: ${t('scanner.lentTo')}${a.lent_to_name ? ` (${a.lent_to_name})` : ''}`); return; }
      }
      if (mode === 'return' && !a.lent_out) { scanFeedback(false); toast.error(`${a.name}: OK`); return; }
      if (mode === 'bihin_stock' && a.bihin_kind !== 'consumable') { scanFeedback(false); toast.error('在庫調整は消耗品のみ可能です'); return; }
      const key = `bihin|${a.id}`;
      setCart((prev) => prev.find((l) => l.key === key) ? prev
        : [...prev, { key, asset_kind: 'bihin', asset_id: a.id, asset_no: a.asset_no, name: a.name,
          bihin_kind: a.bihin_kind, lent_out: a.lent_out, lent_to_name: a.lent_to_name,
          stock_qty: a.stock_qty, uom: a.unit, location: a.location, category: a.category, status: a.status,
          quantity: 1 }]);
      scanFeedback(true, mode);
      return;
    }

    // IT資産 / 設備
    if (mode === 'lend' && a.lent_out) { scanFeedback(false); toast.error(`${a.name}: ${t('scanner.lentTo')}${a.lent_to_name ? ` (${a.lent_to_name})` : ''}`); return; }
    if (mode === 'return' && !a.lent_out) { scanFeedback(false); toast.error(`${a.name}: OK`); return; }
    const key = `${a.asset_kind}|${a.id}`;
    setCart((prev) => prev.find((l) => l.key === key) ? prev
      : [...prev, { key, asset_kind: a.asset_kind, asset_id: a.id, asset_no: a.asset_no, name: a.name,
        lent_out: a.lent_out, lent_to_name: a.lent_to_name, status: a.status,
        assigned_to_name: a.assigned_to_name, location: a.location, category: a.category,
        last_verified_at: a.last_verified_at }]);
    scanFeedback(true, mode);
  }, [isStock, mode, context, t]);
  const onDetected = useCallback(async (code) => {
    try {
      const res = await scannerApi.resolve(code, mode);
      addFromResolve(res);
    } catch (e) {
      scanFeedback(false);
      toast.error(e?.response?.data?.detail || t('scanner.scan.notFound'));
    }
  }, [mode, addFromResolve, t]);

  const updateLine = (key, patch) => setCart((prev) => prev.map((l) => l.key === key ? { ...l, ...patch } : l));
  const removeLine = (key) => setCart((prev) => prev.filter((l) => l.key !== key));

  const buildLines = () => {
    if (mode === 'stock_in') return cart.map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity), location_id: locationId || null, lot_no: l.isLot ? l.lot_no : null }));
    if (mode === 'stock_out') return cart.map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity), location_id: locationId || null }));
    if (mode === 'stocktake') return cart.map((l) => ({ item_id: l.item_id, lot_id: l.isLot ? l.lot_id : null, counted_qty: Number(l.counted_qty), location_id: l.isLot ? null : (locationId || null) }));
    if (mode === 'move') return cart.map((l) => ({ asset_kind: l.asset_kind, asset_id: l.asset_id, location: moveLocation }));
    if (mode === 'verify') return cart.map((l) => ({ asset_kind: l.asset_kind, asset_id: l.asset_id, note: l.note || null }));
    if (mode === 'eng_use' || mode === 'eng_return') return cart.map((l) => ({ asset_kind: 'eng', asset_id: l.asset_id, quantity: Number(l.quantity) }));
    if (mode === 'eng_scrap') return cart.map((l) => ({ asset_kind: 'eng', asset_id: l.asset_id, quantity: Number(l.quantity), reason: l.reason }));
    if (mode === 'bihin_stock') return cart.map((l) => ({ asset_kind: 'bihin', asset_id: l.asset_id, quantity: Number(l.quantity) }));
    // lend / return / assign / lookup — plain asset_kind + asset_id
    return cart.map((l) => ({ asset_kind: l.asset_kind, asset_id: l.asset_id }));
  };

  const commit = async () => {
    if (needsEmployee && !borrower) {
      toast.error(mode === 'assign' ? t('scanner.assignee.required') : t('scanner.borrower.required'));
      return;
    }
    if (mode === 'move' && !moveLocation.trim()) {
      toast.error(t('scanner.location.required'));
      return;
    }
    if (mode === 'stock_out') {
      const bad = tehaiError(tehaiNo, { required: tehaiRequired });
      if (bad) { toast.error(bad); return; }
    }
    setCommitting(true);
    try {
      const res = await scannerApi.commit({
        mode,
        department_id: context === 'mfg' ? activeId : null,
        borrower_employee_id: needsEmployee ? borrower?.id : null,
        // A2: 出庫は理由を一緒に記録する（既定は 製造使用）。§手配番号 は全行に同じ番号。
        ...(mode === 'stock_out'
          ? { reason_code: issueReason,
              tehai_no: normalizeTehai(tehaiNo) || null }
          : {}),
        lines: buildLines(),
      });
      toast.success(`${t(`scanner.mode.${mode}`)} ${t('scanner.committed')} (${res.line_count}${t('scanner.itemsUnit')})`);
      scanCommitFeedback(mode);
      setLastSession(res);
      setCart([]);
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('scanner.error'));
    } finally { setCommitting(false); }
  };

  const undoSession = async (id) => {
    try {
      await scannerApi.undo(id);
      toast.success(t('scanner.undone'));
      if (lastSession?.id === id) setLastSession(null);
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('scanner.error'));
    }
  };

  const printLast = () => {
    const ops = (lastSession?.ops || []).filter((o) => o.kind === 'stock_in');
    if (!ops.length) return;
    const locCode = (locations.find((l) => l.id === locationId) || {}).code || '';
    const deptName = active?.name || '';
    const labels = ops.map((o) => buildLotLabel(
      { name: o.item_name, sku: o.sku, uom: o.uom },
      { lot_no: o.lot_no, quantity: o.quantity },
      locCode, deptName,
    ));
    setLabelData(labels);
  };

  // Hub — ALWAYS the entry screen (spec: reset behavior, no saved context).
  if (context === null) {
    return <ScannerHub availability={availability} hints={hubHints} onPick={pickContext} />;
  }

  if (context === 'mfg' && loading) {
    return <div className="py-24 text-center text-sm text-zinc-400" data-testid="scanner-loading">…</div>;
  }

  // 製造管理は必ず特定の部門内で作業する（全部門モードは廃止）。
  if (context === 'mfg' && (!activeId || isAll)) {
    return (
      <div className="page-enter" data-testid="scanner-app">
        <div className="mb-5 flex justify-end">
          <ContextChip profile={profile} onSwitch={backToHub} />
        </div>
        <DepartmentPicker hideAll />
      </div>
    );
  }

  const theme = profile.theme;

  return (
    <div className="page-enter space-y-3 sm:space-y-5" data-testid="scanner-app">
      {/* Compact mobile-first header — sticky under the global app header so
          the mode + scanner viewport can dominate the visible area. */}
      <div className="sticky top-14 sm:top-16 z-20 -mx-3 sm:mx-0 px-3 sm:px-0 pt-2 pb-2 sm:pt-0 sm:pb-0 bg-[#FAFAFA] sm:bg-transparent border-b border-zinc-200/70 sm:border-b-0">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex items-center gap-2">
            <span className={`inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${theme.iconBg} shrink-0`}>
              <Barcode size={18} weight="duotone" className={theme.icon} />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-2xl font-bold tracking-tight text-zinc-900 truncate leading-tight">
                {t('scanner.title')}
                <span className="text-xs sm:text-sm font-semibold text-zinc-400 ml-1.5">/ {t(profile.titleKey)}</span>
              </h1>
              <p className="hidden sm:block text-sm text-zinc-500 mt-0.5">{t('scanner.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <ContextChip profile={profile} onSwitch={backToHub} />
            {context === 'mfg' && activeId && <DepartmentSelector hideAll />}
          </div>
        </div>
        {/* Quick-jump chips — visually secondary, one-tap access to PO検収 /
            部品入荷 / verify report without leaving the scanner surface. */}
        {(context === 'mfg' || mode === 'verify') && (
          <div className="flex items-center gap-1.5 mt-2 overflow-x-auto no-scrollbar" data-testid="scanner-quick-links">
            {mode === 'verify' && (
              <button type="button" onClick={() => setVerifyReportOpen(true)}
                data-testid="scanner-verify-report-btn"
                className="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-teal-500 text-white text-xs font-semibold shadow-sm active:scale-95">
                <ChartBar size={13} weight="bold" /> 棚卸レポート
              </button>
            )}
            {context === 'mfg' && (
              <>
                <button type="button" onClick={() => navigate('/scanner/po-receive')}
                  data-testid="scanner-po-receive-link"
                  className="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-emerald-500 text-white text-xs font-semibold shadow-sm active:scale-95">
                  <Barcode size={13} weight="bold" /> PO検収
                </button>
                <button type="button" onClick={() => navigate('/parts-request?tab=receive')}
                  data-testid="scanner-parts-receive-link"
                  className="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-orange-500 text-white text-xs font-semibold shadow-sm active:scale-95">
                  <Barcode size={13} weight="bold" /> 部品入荷
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* 使い方ガイド — collapsed by default on mobile so the scanner stays
          above the fold. Kept as a subtle single-line disclosure. */}
      <div className={`rounded-lg border overflow-hidden ${theme.guideBox}`} data-testid="scanner-guide">
        <button type="button" onClick={toggleGuide} data-testid="scanner-guide-toggle"
          className={`w-full flex items-center justify-between px-3 py-1.5 transition-colors ${theme.guideHover}`}>
          <span className={`flex items-center gap-1.5 text-xs font-semibold ${theme.guideHead}`}>
            <Lightbulb size={13} weight="fill" className={theme.guideBulb} /> {t('scanner.guide.title')}
          </span>
          <span className={`flex items-center gap-1 text-[11px] ${theme.guideToggle}`}>
            {showGuide ? t('scanner.guide.hide') : t('scanner.guide.show')}
            {showGuide ? <CaretUp size={11} /> : <CaretDown size={11} />}
          </span>
        </button>
        {showGuide && (
          <ol className="px-4 pb-3 pt-1 space-y-1" data-testid="scanner-guide-body">
            {GUIDE_STEPS.map((k, i) => (
              <li key={k} className="flex gap-2 text-[13px] text-zinc-600">
                <span className={`shrink-0 w-4 h-4 rounded-full text-white text-[10px] font-bold flex items-center justify-center mt-0.5 ${theme.guideStep}`}>{i + 1}</span>
                <span>{t(k)}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Mode grid — 2-cols on phones (larger touch targets), 5-cols on
          tablet+. Selected mode gets a clear filled state. */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="scanner-mode-tabs">
        {availableModes.map((m) => {
          const Icon = MODE_ICON[m];
          const on = mode === m;
          return (
            <button key={m} onClick={() => changeMode(m)} data-testid={`scanner-mode-${m}`}
              className={`flex flex-col items-center justify-center gap-1 h-16 sm:h-14 rounded-xl border text-[13px] sm:text-xs font-bold transition-all active:scale-95 ${
                on ? `${theme.tabActive} shadow-sm` : 'bg-white border-zinc-200 text-zinc-600 active:bg-zinc-50'}`}>
              <Icon size={20} weight={on ? 'fill' : 'duotone'} />
              {t(`scanner.mode.${m}`)}
            </button>
          );
        })}
      </div>

      {/* Post-commit banner */}
      {lastSession && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="scanner-last-banner">
          <span className="text-sm text-emerald-800 font-medium">
            {t(`scanner.mode.${lastSession.mode}`)} · {lastSession.line_count}{t('scanner.itemsUnit')} {t('scanner.committed')}
          </span>
          <div className="flex items-center gap-2">
            {lastSession.mode === 'stock_in' && (
              <Button variant="outline" size="sm" onClick={printLast} data-testid="scanner-print-labels" className="h-8 bg-white">
                <Printer size={14} className="mr-1" /> {t('scanner.printLabels')}
              </Button>
            )}
            {lastSession.mode !== 'lookup' && (
              <Button variant="outline" size="sm" onClick={() => undoSession(lastSession.id)} data-testid="scanner-undo-last" className="h-8 bg-white text-rose-600 border-rose-200 hover:bg-rose-50">
                <ArrowCounterClockwise size={14} className="mr-1" /> {t('scanner.undo')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Mode-specific hint bars */}
      {mode === 'verify' && (
        <div className="rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2 text-xs text-teal-800" data-testid="scanner-verify-hint">
          {t('scanner.verify.hint')}
        </div>
      )}
      {mode === 'lookup' && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="scanner-lookup-hint">
          {t('scanner.lookup.hint')}
        </div>
      )}
      {mode === 'eng_scrap' && (
        <div className="rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2 text-xs text-rose-800" data-testid="scanner-scrap-hint">
          廃棄には各行に理由の入力が必要です
        </div>
      )}
      {mode === 'bihin_stock' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800" data-testid="scanner-bihin-stock-hint">
          消耗品の在庫を増減します（マイナス値で出庫）
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 items-start">
        {/* Left: scan */}
        <div className="space-y-3">
          {needsEmployee && (
            <div data-testid="scanner-borrower-block">
              <label className="text-xs font-semibold text-zinc-700 tracking-wide">
                {mode === 'assign' ? t('scanner.assignee.label') : t('scanner.borrower.label')}
              </label>
              <div className="mt-1"><BorrowerPicker value={borrower} onSelect={setBorrower} /></div>
            </div>
          )}
          {mode === 'move' && (
            <div data-testid="scanner-move-loc-block">
              <label className="text-xs font-semibold text-zinc-700 tracking-wide">{t('scanner.location.label')}</label>
              <Input
                data-testid="scanner-move-location"
                value={moveLocation}
                onChange={(e) => setMoveLocation(e.target.value)}
                placeholder={t('scanner.location.placeholder')}
                className="h-10 mt-1"
              />
            </div>
          )}
          {isStock && locations.length > 0 && (
            <div data-testid="scanner-location-block">
              <label className="text-xs font-semibold text-zinc-700 tracking-wide">{t('scanner.cart.location')}</label>
              <Select value={locationId || 'none'} onValueChange={(v) => setLocationId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="scanner-location-select" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('scanner.cart.noLocation')}</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.picker_label || `${l.code}${l.name ? ` — ${l.name}` : ''}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {mode === 'stock_out' && (
            <div data-testid="scanner-issue-reason-block">
              <label className="text-xs font-semibold text-zinc-700 tracking-wide">{t('mfg.issue.reason')}</label>
              <Select value={issueReason} onValueChange={setIssueReason}>
                <SelectTrigger data-testid="scanner-issue-reason-select" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="production">{t('mfg.issue.reason.production')}</SelectItem>
                  <SelectItem value="prototype">{t('mfg.issue.reason.prototype')}</SelectItem>
                  <SelectItem value="scrap">{t('mfg.issue.reason.scrap')}</SelectItem>
                  <SelectItem value="return">{t('mfg.issue.reason.return')}</SelectItem>
                  <SelectItem value="other">{t('mfg.issue.reason.other')}</SelectItem>
                </SelectContent>
              </Select>
              <div className="mt-3" data-testid="scanner-tehai-block">
                <label className="text-xs font-semibold tracking-wide text-zinc-700">
                  手配番号（このカート全体）{tehaiRequired && <span className="text-rose-500">＊</span>}
                </label>
                <Input value={tehaiNo} onChange={(e) => setTehaiNo(e.target.value)}
                  placeholder="例 026100105（スキャンまたは入力）" inputMode="numeric"
                  className="mt-1 h-11 font-mono" data-testid="scanner-tehai-input" />
                <p className="mt-0.5 text-[11px] text-zinc-500">
                  {TEHAI_HINT}。
                  {tehaiRequired ? '製造使用・試作使用では必須です。' : 'この理由では任意です。'}
                  カートの全行に同じ番号が付きます。
                </p>
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3 sm:p-4">
            <ScannerScanner onDetected={onDetected} />
          </div>
        </div>

        {/* Right: cart */}
        <ScannerCart mode={mode} cart={cart} onUpdate={updateLine} onRemove={removeLine}
          onClear={() => setCart([])} onCommit={commit} committing={committing} />
      </div>

      {/* Recent sessions */}
      <div className="pt-2" data-testid="scanner-sessions">
        <h2 className="text-sm font-bold text-zinc-700 mb-2">{t('scanner.sessions.title')}</h2>
        <div className="bg-white rounded-xl border border-zinc-200 divide-y divide-zinc-200 overflow-hidden">
          {sessions.length === 0 && <p className="px-4 py-6 text-center text-sm text-zinc-400">{t('scanner.sessions.empty')}</p>}
          {sessions.map((s) => (
            <div key={s.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`scanner-session-${s.id}`}>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${MODE_BADGE[s.mode] || 'bg-zinc-50 text-zinc-600 border-zinc-200'}`}>{t(`scanner.mode.${s.mode}`)}</span>
              <span className="text-sm text-zinc-700">{s.line_count}{t('scanner.itemsUnit')}</span>
              {s.borrower_name && <span className="text-xs text-zinc-400 truncate">→ {s.borrower_name}</span>}
              <span className="ml-auto text-[11px] text-zinc-400">{formatShort(s.created_at)}</span>
              {s.can_undo
                ? <button onClick={() => undoSession(s.id)} data-testid={`scanner-session-undo-${s.id}`} className="text-rose-500 hover:text-rose-700 shrink-0"><ArrowCounterClockwise size={16} /></button>
                : <span className="text-[10px] text-zinc-400 shrink-0">{s.mode === 'lookup' ? '—' : t('scanner.sessions.undone')}</span>}
            </div>
          ))}
        </div>
      </div>
      <LabelPrintDialog open={!!labelData} onClose={() => setLabelData(null)} labels={labelData || []} title="ロットラベル" />
      <VerifyReportDialog open={verifyReportOpen} onClose={() => setVerifyReportOpen(false)} />
    </div>
  );
}

export default function ScannerApp() {
  return (
    <DepartmentProvider>
      <ScannerInner />
    </DepartmentProvider>
  );
}
